import React, { useState, useMemo, useCallback } from 'react';
import {
  Paper,
  Card,
  Text,
  Badge,
  Group,
  ActionIcon,
  ScrollArea,
  Stack,
  Button,
  Modal,
  TextInput,
  Select,
  Box,
  SimpleGrid,
  Flex,
  Menu,
  Checkbox,
} from '@mantine/core';
import {
  IconGripVertical,
  IconDots,
  IconEdit,
  IconTrash,
  IconCopy,
  IconPlus,
  IconChevronDown,
} from '@tabler/icons-react';
import { DatabaseItem, Database, PropertyDefinition } from '../../../types/models/Database';
import { PropertyValue } from '../../../types/models/Property';

interface KanbanViewProps {
  database: Database;
  items: DatabaseItem[];
  groupByPropertyId?: string;
  onItemSelect?: (item: DatabaseItem) => void;
  onItemCreate?: (item: DatabaseItem) => void;
  onItemUpdate?: (item: DatabaseItem) => void;
  onItemDelete?: (itemId: string) => void;
  onItemDuplicate?: (item: DatabaseItem) => void;
}

interface KanbanColumn {
  id: string;
  name: string;
  color: string;
  items: DatabaseItem[];
}

/**
 * Kanban View Component
 * Displays database items as cards in columns grouped by a select/status property
 */
export const KanbanView: React.FC<KanbanViewProps> = ({
  database,
  items,
  groupByPropertyId,
  onItemSelect,
  onItemCreate,
  onItemUpdate,
  onItemDelete,
  onItemDuplicate,
}) => {
  const [draggedItem, setDraggedItem] = useState<DatabaseItem | null>(null);
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [newItemTitle, setNewItemTitle] = useState('');
  const [selectedColumn, setSelectedColumn] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<DatabaseItem | null>(null);

  // Find the grouping property
  const groupProperty = useMemo(() => {
    if (!groupByPropertyId) return null;
    return database.schema.properties.find(p => p.id === groupByPropertyId);
  }, [database.schema.properties, groupByPropertyId]);

  // Build kanban columns
  const columns: KanbanColumn[] = useMemo(() => {
    // DEBUG
    console.log('[KanbanView] groupByPropertyId:', groupByPropertyId, 'groupProperty:', groupProperty, 'items count:', items.length);
    if (items.length > 0) {
      console.log('[KanbanView] first item properties:', JSON.stringify(items[0].properties));
    }

    if (!groupProperty) {
      // Default to status-like columns if no property specified
      return [
        { id: 'active', name: 'Active', color: '#52b788', items: items },
        { id: 'completed', name: 'Completed', color: '#588b8b', items: [] },
        { id: 'archived', name: 'Archived', color: '#adb5bd', items: [] },
      ];
    }

    // Special case: date property — group by the date value itself.
    // Each unique date becomes a column.
    if (groupProperty.type === 'date') {
      const dateColumnMap = new Map<string, { name: string; color: string; items: DatabaseItem[] }>();
      items.forEach(item => {
        const propValue = item.properties.find(p => p.propertyId === groupByPropertyId);
        const raw = propValue?.value;
        console.log('[KanbanView] date item:', item.title, 'raw:', raw, 'parsed:', raw ? new Date(raw as string).toISOString() : 'invalid');
        if (raw) {
          const d = new Date(raw as string);
          if (!isNaN(d.getTime())) {
            const key = d.toISOString().slice(0, 10); // YYYY-MM-DD
            if (!dateColumnMap.has(key)) {
              dateColumnMap.set(key, {
                name: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }),
                color: '#94a3b8',
                items: [],
              });
            }
            dateColumnMap.get(key)!.items.push(item);
          }
        }
      });
      console.log('[KanbanView] date column map keys:', Array.from(dateColumnMap.keys()));
      // Sort columns by date descending (most recent first)
      const sortedKeys = Array.from(dateColumnMap.keys()).sort((a, b) => b.localeCompare(a));
      return sortedKeys.map(key => ({ id: key, ...dateColumnMap.get(key)! }));
    }

    // Default: select or status property — use the options.
    const columnMap = new Map<string, { name: string; color: string; items: DatabaseItem[] }>();
    const options = groupProperty.options || groupProperty.statusOptions || [];
    options.forEach(option => {
      columnMap.set(option.id, {
        name: option.name,
        color: option.color,
        items: [],
      });
    });

    // Distribute items into columns
    items.forEach(item => {
      const propValue = item.properties.find(p => p.propertyId === groupByPropertyId);
      if (propValue) {
        const columnData = columnMap.get(String(propValue.value));
        if (columnData) {
          columnData.items.push(item);
        }
      }
    });

    return Array.from(columnMap.entries()).map(([id, data]) => ({
      id,
      ...data,
    }));
  }, [groupProperty, items, groupByPropertyId]);

  // Get property value for display
  const getPropertyValue = useCallback((item: DatabaseItem, propId: string): string => {
    const prop = item.properties.find(p => p.propertyId === propId);
    if (!prop) return '-';
    return String(prop.value);
  }, []);

  // Get property by ID
  const getProperty = useCallback((propId: string): PropertyDefinition | undefined => {
    return database.schema.properties.find(p => p.id === propId);
  }, [database.schema.properties]);

  // Handle drag start
  const handleDragStart = (e: React.DragEvent, item: DatabaseItem) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Handle drag over
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Handle drop
  const handleDrop = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    if (!draggedItem || !groupByPropertyId) return;

    // Update item with new property value
    const updatedItem = {
      ...draggedItem,
      properties: draggedItem.properties.map(p =>
        p.propertyId === groupByPropertyId ? { ...p, value: columnId } : p
      ),
      updatedAt: new Date(),
    };

    onItemUpdate?.(updatedItem);
    setDraggedItem(null);
  };

  // Handle add item to column
  const handleAddItemToColumn = (columnId: string) => {
    setSelectedColumn(columnId);
    setIsAddingItem(true);
    setNewItemTitle('');
  };

  // Handle create new item
  const handleCreateItem = () => {
    if (!newItemTitle.trim() || !selectedColumn) return;

    const newItem: DatabaseItem = {
      id: `item_${Date.now()}`,
      databaseId: database.id,
      title: newItemTitle,
      properties: groupByPropertyId
        ? [{ propertyId: groupByPropertyId, value: selectedColumn }]
        : [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    onItemCreate?.(newItem);
    setIsAddingItem(false);
    setNewItemTitle('');
    setSelectedColumn(null);
  };

  // Handle delete item
  const handleDeleteItem = (itemId: string) => {
    if (confirm('Are you sure you want to delete this item?')) {
      onItemDelete?.(itemId);
    }
  };

  // Render single card
  const renderCard = (item: DatabaseItem) => (
    <Card
      key={item.id}
      p="sm"
      radius="sm"
      withBorder
      draggable
      onDragStart={(e) => handleDragStart(e, item)}
      style={{ cursor: 'grab', opacity: draggedItem?.id === item.id ? 0.5 : 1 }}
    >
      <Group justify="space-between" align="flex-start" mb="xs">
        <Stack gap={0} style={{ flex: 1 }}>
          <Text size="sm" fw={500} lineClamp={2}>
            {item.title}
          </Text>
          <Text size="xs" c="dimmed">
            {item.createdAt.toLocaleDateString()}
          </Text>
        </Stack>
        <Menu shadow="md" position="bottom-end">
          <Menu.Target>
            <ActionIcon size="xs" variant="subtle" color="gray">
              <IconDots size={14} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconEdit size={14} />}
              onClick={() => setEditingItem(item)}
            >
              Edit
            </Menu.Item>
            <Menu.Item
              leftSection={<IconCopy size={14} />}
              onClick={() => onItemDuplicate?.(item)}
            >
              Duplicate
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item
              leftSection={<IconTrash size={14} />}
              color="red"
              onClick={() => handleDeleteItem(item.id)}
            >
              Delete
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>

      {/* Property previews */}
      {item.properties.length > 0 && (
        <Stack gap="xs" mt="xs">
          {item.properties.slice(0, 2).map(prop => {
            const propDef = getProperty(prop.propertyId);
            if (!propDef || propDef.type === groupProperty?.id) return null;
            return (
              <Group key={prop.propertyId} justify="space-between" gap="xs">
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {propDef.name}:
                </Text>
                <Badge size="sm" variant="light">
                  {String(prop.value).slice(0, 15)}
                </Badge>
              </Group>
            );
          })}
        </Stack>
      )}
    </Card>
  );

  return (
    <div className="kanban-view-wrapper" style={{ width: '100%', height: '100%' }}>
      <ScrollArea type="auto" style={{ width: '100%', height: 'calc(100vh - 180px)' }}>
        <Flex gap="md" p="md" style={{ minWidth: 'fit-content' }}>
          {columns.map(column => (
            <Paper
              key={column.id}
              p="md"
              radius="md"
              style={{
                minWidth: '320px',
                maxHeight: 'calc(100vh - 240px)',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#f8f9fa',
              }}
            >
              {/* Column header */}
              <Group justify="space-between" mb="md" align="center">
                <Group gap="sm" align="center">
                  <div
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '4px',
                      backgroundColor: column.color,
                    }}
                  />
                  <Stack gap={0}>
                    <Text fw={600} size="sm">
                      {column.name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {column.items.length} items
                    </Text>
                  </Stack>
                </Group>
              </Group>

              {/* Items area */}
              <Stack
                gap="sm"
                style={{
                  flex: 1,
                  minHeight: '200px',
                  overflowY: 'auto',
                  backgroundColor: 'transparent',
                }}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, column.id)}
              >
                {column.items.map(item => renderCard(item))}

                {column.items.length === 0 && draggedItem === null && (
                  <Box
                    style={{
                      textAlign: 'center',
                      padding: '20px',
                      color: '#adb5bd',
                      minHeight: '150px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Text size="sm" c="dimmed">
                      No items yet
                    </Text>
                  </Box>
                )}
              </Stack>

              {/* Add button */}
              <Button
                variant="light"
                size="sm"
                fullWidth
                mt="md"
                leftSection={<IconPlus size={14} />}
                onClick={() => handleAddItemToColumn(column.id)}
              >
                Add item
              </Button>
            </Paper>
          ))}
        </Flex>
      </ScrollArea>

      {/* Add Item Modal */}
      <Modal
        opened={isAddingItem}
        onClose={() => setIsAddingItem(false)}
        title="Add Item"
        size="sm"
      >
        <Stack>
          <TextInput
            placeholder="Item title"
            label="Title"
            value={newItemTitle}
            onChange={(e) => setNewItemTitle(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateItem();
            }}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" onClick={() => setIsAddingItem(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateItem} disabled={!newItemTitle.trim()}>
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Edit Item Modal */}
      <Modal
        opened={!!editingItem}
        onClose={() => setEditingItem(null)}
        title="Edit Item"
        size="sm"
      >
        {editingItem && (
          <Stack>
            <TextInput
              label="Title"
              value={editingItem.title}
              onChange={(e) =>
                setEditingItem({
                  ...editingItem,
                  title: e.currentTarget.value,
                })
              }
            />
            <Group justify="flex-end" gap="sm">
              <Button variant="subtle" onClick={() => setEditingItem(null)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (editingItem) {
                    onItemUpdate?.(editingItem);
                    setEditingItem(null);
                  }
                }}
              >
                Save
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </div>
  );
};

export default KanbanView;
